import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  IoPencilOutline, 
  IoTrashOutline,
  IoCheckmarkCircle,
  IoEllipseOutline,
  IoAddOutline
} from 'react-icons/io5';
import { RubricCriterion } from '../../hooks/useRubric';
import { useDarkMode } from '../../context/DarkModeContext';
import { RubricItem } from './RubricCategoryCard';

interface RubricCriterionItemProps {
  criterion: RubricItem;
  categoryId: string;
  categoryName: string;
  onUpdate: (criterionId: string, updates: Partial<RubricCriterion>) => Promise<boolean>;
  onDelete: (criterionId: string) => Promise<boolean>;
  onUpdateEvidenceTarget?: (criterionId: string, target: number) => Promise<boolean>;
  isEditingAny: boolean;
  onEditStart: () => void;
  onEditEnd: () => void;
  onQuickAdd?: (rubricItemId?: string, category?: string, description?: string) => void;
}

const RubricCriterionItem: React.FC<RubricCriterionItemProps> = ({
  criterion,
  categoryId,
  categoryName,
  onUpdate,
  onDelete,
  onUpdateEvidenceTarget,
  isEditingAny,
  onEditStart,
  onEditEnd,
  onQuickAdd
}) => {
  const { darkMode } = useDarkMode();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editEvidenceTarget, setEditEvidenceTarget] = useState<number | null>(null);
  const [showActions, setShowActions] = useState(false);

  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = darkMode ? 'bg-gray-900' : 'bg-white';
  const completedColor = darkMode ? 'text-green-400' : 'text-green-600';
  const incompleteColor = darkMode ? 'text-gray-500' : 'text-gray-400';

  const handleEditStart = () => {
    setIsEditing(true);
    setEditText(criterion.text);
    setEditDescription(''); // We don't have description in RubricItem, but support it for future
    setEditEvidenceTarget((criterion as any).evidence_target || null);
    onEditStart();
  };

  const handleSave = async () => {
    if (!editText.trim()) return;

    const success = await onUpdate(criterion.id, {
      text: editText.trim(),
      description: editDescription.trim() || undefined
    });

    if (success && onUpdateEvidenceTarget && editEvidenceTarget !== null) {
      // Update evidence target if it was changed
      const currentTarget = (criterion as any).evidence_target;
      if (editEvidenceTarget !== currentTarget) {
        await onUpdateEvidenceTarget(criterion.id, editEvidenceTarget);
      }
    }

    if (success) {
      setIsEditing(false);
      onEditEnd();
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditText('');
    setEditDescription('');
    onEditEnd();
  };

  const handleDelete = async () => {
    // Show warning if has evidence
    if (criterion.hasEvidence) {
      const evidenceCount = (criterion.careerCardCount || 0) + (criterion.noteCount || 0);
      const confirmed = window.confirm(
        `This criterion has ${evidenceCount} piece${evidenceCount !== 1 ? 's' : ''} of evidence linked. ` +
        `Deleting it will remove these links. Continue?`
      );
      if (!confirmed) return;
    }

    await onDelete(criterion.id);
  };

  if (isEditing) {
    return (
      <motion.div
        layout
        className={`p-3 rounded-lg border ${borderColor} bg-blue-50 dark:bg-blue-900/20`}
      >
        <div className="space-y-2">
          <input
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className={`w-full px-3 py-2 rounded border ${borderColor} ${inputBg} ${textColor} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder="Criterion text"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSave();
              } else if (e.key === 'Escape') {
                handleCancel();
              }
            }}
          />
          <input
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder="Description (optional)"
            className={`w-full px-3 py-2 rounded border ${borderColor} ${inputBg} ${textColor} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          {onUpdateEvidenceTarget && (
            <div className="flex items-center gap-2">
              <span className={`text-xs ${mutedTextColor}`}>Evidence target:</span>
              <input
                type="number"
                value={editEvidenceTarget || ''}
                onChange={(e) => {
                  const val = e.target.value ? Math.max(1, Math.min(10, Number(e.target.value))) : null;
                  setEditEvidenceTarget(val);
                }}
                placeholder="inherit"
                className={`w-20 px-2 py-1 rounded border ${borderColor} ${inputBg} ${textColor} text-center text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                min="1"
                max="10"
                title="Number of evidence cards required for 100% coverage (leave empty to inherit from category)"
              />
              <span className={`text-xs ${mutedTextColor}`}>cards</span>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={handleCancel}
              className={`px-3 py-1 rounded text-sm ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!editText.trim()}
              className={`px-3 py-1 rounded text-sm ${
                editText.trim()
                  ? 'bg-blue-500 hover:bg-blue-600 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Save
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      layout
      className={`group flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-all ${
        isEditingAny ? 'opacity-50' : ''
      }`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Completion indicator */}
      {criterion.hasEvidence ? (
        <IoCheckmarkCircle className={`w-5 h-5 ${completedColor} flex-shrink-0`} />
      ) : (
        <IoEllipseOutline className={`w-5 h-5 ${incompleteColor} flex-shrink-0`} />
      )}

      {/* Criterion text */}
      <div className="flex-1 min-w-0">
        <p className={`${textColor} text-sm`}>{criterion.text}</p>
        {criterion.hasEvidence && (
          <p className={`text-xs ${mutedTextColor} mt-0.5`}>
            {criterion.careerCardCount ? `${criterion.careerCardCount} card${criterion.careerCardCount !== 1 ? 's' : ''}` : ''}
            {criterion.careerCardCount && criterion.noteCount ? ', ' : ''}
            {criterion.noteCount ? `${criterion.noteCount} note${criterion.noteCount !== 1 ? 's' : ''}` : ''}
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div className={`flex items-center gap-1 transition-opacity ${showActions ? 'opacity-100' : 'opacity-0'}`}>
        {onQuickAdd && (
          <button
            onClick={() => onQuickAdd(criterion.id, categoryName, criterion.text)}
            className={`px-2 py-0.5 rounded text-xs font-medium transition-colors flex items-center gap-1 ${
              darkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
            title="Add evidence for this criterion"
          >
            <IoAddOutline className="w-3 h-3" />
            Add Evidence
          </button>
        )}
        <button
          onClick={handleEditStart}
          className={`p-1 rounded transition-colors ${mutedTextColor} hover:text-blue-500`}
          title="Edit criterion"
        >
          <IoPencilOutline className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={handleDelete}
          className={`p-1 rounded transition-colors ${mutedTextColor} hover:text-red-500`}
          title="Delete criterion"
        >
          <IoTrashOutline className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
};

export default RubricCriterionItem;